import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLElement} [viewMorePrevious] - The previous-page sentinel.
 * @property {HTMLElement} [viewMoreNext] - The next-page sentinel.
 * @property {HTMLElement} [infiniteScrollStatus] - Live loading status.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {string | null} */
  #imageRatioSetting = null;

  /** @type {string | null} */
  #forwardNextPageURL = null;

  /** @type {string | null} */
  #forwardRequestContext = null;

  /** @type {HTMLElement[]} */
  #forwardSoldOutBuffer = [];

  /** @type {Set<string>} */
  #forwardSeenProductIds = new Set();

  #forwardPageIsLoading = false;

  #forwardPageFailed = false;

  #forwardGeneration = 0;

  #forwardStructureObserver = new MutationObserver((mutations) => {
    if (!this.#isForwardBufferedScroll()) return;

    const grid = this.querySelector('[ref="grid"]');
    const structureChanged = mutations.some((mutation) => {
      if (mutation.type === 'attributes') {
        return mutation.target instanceof HTMLElement && mutation.target.getAttribute('ref') === 'viewMoreNext';
      }

      if (mutation.type !== 'childList') return false;

      const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];
      const gridItemsChanged =
        mutation.target === grid &&
        changedNodes.some((node) => node instanceof HTMLElement && node.getAttribute('ref') === 'cards[]');

      if (gridItemsChanged) return true;

      return changedNodes.some(
        (node) =>
          node instanceof HTMLElement &&
          (node.matches('[ref="grid"], [ref="viewMoreNext"]') ||
            node.querySelector('[ref="grid"], [ref="viewMoreNext"]'))
      );
    });

    if (structureChanged) this.#resetForwardBufferedScroll();
  });

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();

    if (this.#isForwardBufferedScroll()) {
      this.#forwardGeneration += 1;
      this.infinityScrollObserver?.disconnect();
      this.#forwardStructureObserver.disconnect();
      this.#forwardSoldOutBuffer = [];
      this.#forwardSeenProductIds.clear();
      this.#forwardNextPageURL = null;
      this.#forwardRequestContext = null;
      this.#forwardPageIsLoading = false;
      this.#forwardPageFailed = false;
      this.removeAttribute('aria-busy');
      this.removeAttribute('data-sold-out-released');
      this.removeAttribute('data-infinite-scroll-ready');
      this.#initializeForwardBufferedScroll();
      this.#observeForwardStructureChanges();
      return;
    }

    const { viewMorePrevious, viewMoreNext } = this.refs;

    // A results list without sentinels uses server-rendered pagination links.
    // Do not prefetch adjacent pages or install an observer in that mode.
    if (!viewMorePrevious && !viewMoreNext) return;

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
  }

  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    this.#forwardGeneration += 1;
    this.#forwardStructureObserver.disconnect();
    const { grid } = this.refs;
    if (grid instanceof HTMLUListElement && this.#forwardSoldOutBuffer.length) {
      grid.append(...this.#forwardSoldOutBuffer);
    }
    this.#forwardSoldOutBuffer = [];
    this.#forwardSeenProductIds.clear();
    this.#forwardNextPageURL = null;
    this.#forwardRequestContext = null;
    this.#forwardPageIsLoading = false;
    this.#forwardPageFailed = false;
  }

  #isForwardBufferedScroll() {
    return this.getAttribute('infinite-scroll-mode') === 'forward-buffered';
  }

  #observeForwardStructureChanges() {
    this.#forwardStructureObserver.disconnect();
    this.#forwardStructureObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-next-page-url'],
    });
  }

  /**
   * Pause external structure detection around this component's own synchronous
   * append/detach work, then resume it before yielding back to the browser.
   *
   * @template T
   * @param {() => T} callback
   * @returns {T}
   */
  #withForwardStructureObserverPaused(callback) {
    this.#forwardStructureObserver.disconnect();

    try {
      return callback();
    } finally {
      this.#forwardStructureObserver.takeRecords();
      if (this.isConnected && this.#isForwardBufferedScroll()) this.#observeForwardStructureChanges();
    }
  }

  #resetForwardBufferedScroll() {
    super.updatedCallback();
    this.#forwardGeneration += 1;
    this.infinityScrollObserver?.disconnect();
    this.#forwardSoldOutBuffer = [];
    this.#forwardSeenProductIds.clear();
    this.#forwardNextPageURL = null;
    this.#forwardRequestContext = null;
    this.#forwardPageIsLoading = false;
    this.#forwardPageFailed = false;
    this.removeAttribute('aria-busy');
    this.removeAttribute('data-sold-out-released');
    this.removeAttribute('data-infinite-scroll-ready');

    const currentSentinel = this.querySelector('[ref="viewMoreNext"]');
    if (currentSentinel instanceof HTMLElement) {
      currentSentinel.classList.remove('is-error', 'is-complete');
    }

    this.#initializeForwardBufferedScroll();
    this.setAttribute('initialized', '');
    this.#observeForwardStructureChanges();
  }

  /**
   * Collection-only loading mode. Available products are appended immediately,
   * while sold-out products stay detached until the final source page is reached.
   * This creates one stable available -> sold-out stream without reordering cards
   * that are already visible.
   */
  #initializeForwardBufferedScroll() {
    const { grid, viewMoreNext } = this.refs;
    if (!(grid instanceof HTMLUListElement)) return;

    this.#forwardRequestContext = this.#getForwardRequestContext(window.location.href);

    this.#collectForwardPageItems(Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')), false);

    if (!(viewMoreNext instanceof HTMLElement) || !viewMoreNext.dataset.nextPageUrl) {
      this.#releaseForwardSoldOutBuffer();
      this.#processNewElements();
      this.setAttribute('data-infinite-scroll-ready', '');
      return;
    }

    this.#forwardNextPageURL = viewMoreNext.dataset.nextPageUrl;
    const scrollRoot = this.#getForwardScrollRoot();

    if (typeof IntersectionObserver !== 'function') {
      this.#forwardPageFailed = true;
      this.#releaseForwardSoldOutBuffer();
      viewMoreNext.classList.add('is-error');
      this.#setForwardStatus('Automatic product loading is not supported. Use the link to continue.');
      this.setAttribute('data-infinite-scroll-ready', '');
      return;
    }

    this.infinityScrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#loadNextForwardPage();
      },
      {
        root: scrollRoot,
        rootMargin: '600px 0px',
      }
    );

    this.infinityScrollObserver.observe(viewMoreNext);
    this.setAttribute('data-infinite-scroll-ready', '');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getForwardScrollRoot() {
    const pageWrapper = this.closest('.page-wrapper');
    if (!(pageWrapper instanceof HTMLElement)) return null;

    const overflowY = getComputedStyle(pageWrapper).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll' ? pageWrapper : null;
  }

  /**
   * Filter/sort identity for a pagination request. Page, Section Rendering, and
   * the presentational grid parameter are excluded so page N and N+1 share a context.
   *
   * @param {string} urlValue
   * @returns {string}
   */
  #getForwardRequestContext(urlValue) {
    const url = new URL(urlValue, window.location.origin);
    url.hash = '';
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.delete('grid');
    url.searchParams.sort();
    return `${url.pathname}?${url.searchParams.toString()}`;
  }

  /**
   * @param {Element[]} items
   * @param {boolean} appendAvailable
   * @returns {{ available: number, soldOut: number }}
   */
  #collectForwardPageItems(items, appendAvailable) {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLUListElement)) return { available: 0, soldOut: 0 };

    return this.#withForwardStructureObserverPaused(() => {
      let available = 0;
      let soldOut = 0;

      for (const item of items) {
        if (!(item instanceof HTMLElement)) continue;

        const productId = item.dataset.productId;
        if (productId && this.#forwardSeenProductIds.has(productId)) {
          item.remove();
          continue;
        }
        if (productId) this.#forwardSeenProductIds.add(productId);

        if (item.classList.contains('product-grid__item--sold-out')) {
          item.remove();
          this.#forwardSoldOutBuffer.push(item);
          soldOut += 1;
        } else {
          if (appendAvailable) grid.append(item);
          available += 1;
        }
      }

      return { available, soldOut };
    });
  }

  async #loadNextForwardPage() {
    const { grid, viewMoreNext } = this.refs;
    const requestedPageURL = this.#forwardNextPageURL;

    if (
      !(grid instanceof HTMLUListElement) ||
      !(viewMoreNext instanceof HTMLElement) ||
      !requestedPageURL ||
      this.#forwardPageIsLoading ||
      this.#forwardPageFailed
    ) {
      return;
    }

    this.#forwardPageIsLoading = true;
    const requestGeneration = this.#forwardGeneration;
    const requestContext = this.#forwardRequestContext;
    this.setAttribute('aria-busy', 'true');
    this.#setForwardStatus('Loading more products.');

    try {
      const pageURL = new URL(requestedPageURL, window.location.origin);
      pageURL.hash = '';
      const pageHTML = await sectionRenderer.getSectionHTML(this.sectionId, false, pageURL);

      // A filter/sort morph disconnects this instance. Ignore its stale response.
      if (
        !this.isConnected ||
        requestGeneration !== this.#forwardGeneration ||
        !requestContext ||
        requestContext !== this.#getForwardRequestContext(window.location.href) ||
        requestedPageURL !== this.#forwardNextPageURL
      ) {
        return;
      }

      const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
      const nextGrid = parsedPage.querySelector('results-list[infinite-scroll-mode="forward-buffered"] [ref="grid"]');
      if (!(nextGrid instanceof HTMLUListElement)) throw new Error('The next collection grid was not returned');

      const pageItems = Array.from(nextGrid.querySelectorAll(':scope > [ref="cards[]"]'));
      const added = this.#collectForwardPageItems(pageItems, true);
      const nextSentinel = parsedPage.querySelector(
        'results-list[infinite-scroll-mode="forward-buffered"] [ref="viewMoreNext"]'
      );
      const nextPageURL = nextSentinel instanceof HTMLElement ? nextSentinel.dataset.nextPageUrl : null;
      let releasedSoldOut = 0;

      this.#forwardNextPageURL = nextPageURL || null;

      if (this.#forwardNextPageURL) {
        this.#withForwardStructureObserverPaused(() => {
          viewMoreNext.dataset.nextPageUrl = this.#forwardNextPageURL || '';
          const fallbackLink = viewMoreNext.querySelector('.product-grid__infinite-scroll-fallback');
          if (fallbackLink instanceof HTMLAnchorElement) fallbackLink.href = this.#forwardNextPageURL || '';
        });
      } else {
        releasedSoldOut = this.#releaseForwardSoldOutBuffer();
        this.infinityScrollObserver?.disconnect();
        this.#withForwardStructureObserverPaused(() => {
          viewMoreNext.classList.add('is-complete');
          viewMoreNext.removeAttribute('data-next-page-url');
        });
      }

      this.#processNewElements();
      this.#setForwardStatus(`${added.available + releasedSoldOut} additional products loaded.`);
    } catch (error) {
      if (
        !this.isConnected ||
        requestGeneration !== this.#forwardGeneration ||
        !requestContext ||
        requestContext !== this.#getForwardRequestContext(window.location.href) ||
        requestedPageURL !== this.#forwardNextPageURL
      ) {
        return;
      }

      this.#forwardPageFailed = true;
      this.infinityScrollObserver?.disconnect();
      const releasedSoldOut = this.#releaseForwardSoldOutBuffer();
      this.#processNewElements();
      viewMoreNext.classList.add('is-error');
      this.#setForwardStatus(
        `Products could not be loaded automatically.${releasedSoldOut ? ` ${releasedSoldOut} sold-out products were restored.` : ''}`
      );
      console.error('Collection infinite scroll failed', error);
    } finally {
      if (requestGeneration !== this.#forwardGeneration) return;

      this.#forwardPageIsLoading = false;
      this.removeAttribute('aria-busy');

      if (requestContext !== this.#getForwardRequestContext(window.location.href)) return;

      if (this.isConnected && this.#forwardNextPageURL && !this.#forwardPageFailed) {
        requestAnimationFrame(() => {
          if (this.#isForwardSentinelNearViewport()) this.#loadNextForwardPage();
        });
      }
    }
  }

  /**
   * @returns {number}
   */
  #releaseForwardSoldOutBuffer() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLUListElement)) return 0;

    const soldOutCount = this.#forwardSoldOutBuffer.length;
    this.#withForwardStructureObserverPaused(() => {
      this.setAttribute('data-sold-out-released', '');
      if (soldOutCount) grid.append(...this.#forwardSoldOutBuffer);
    });
    this.#forwardSoldOutBuffer = [];
    return soldOutCount;
  }

  #isForwardSentinelNearViewport() {
    const { viewMoreNext } = this.refs;
    if (!(viewMoreNext instanceof HTMLElement) || !viewMoreNext.isConnected) return false;

    const sentinelRect = viewMoreNext.getBoundingClientRect();
    const scrollRoot = this.#getForwardScrollRoot();
    const rootRect = scrollRoot?.getBoundingClientRect();
    const viewportTop = rootRect?.top ?? 0;
    const viewportBottom = rootRect?.bottom ?? window.innerHeight;

    return sentinelRect.top <= viewportBottom + 600 && sentinelRect.bottom >= viewportTop - 600;
  }

  /**
   * @param {string} message
   */
  #setForwardStatus(message) {
    const { infiniteScrollStatus } = this.refs;
    if (infiniteScrollStatus instanceof HTMLElement) infiniteScrollStatus.textContent = message;
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious || !viewMoreNext) return;

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        // Wait for any in-progress view transitions to finish
        if (viewTransition.current) await viewTransition.current;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (entry.target === viewMorePrevious) {
              this.#renderPreviousPage();
            } else {
              this.#renderNextPage();
            }
          }
        }
      },
      {
        rootMargin: '100px',
      }
    );

    this.infinityScrollObserver.observe(viewMorePrevious);
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * @param {Object} pageInfo - The page info
   * @param {number} pageInfo.page - The page number
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    if (pageInfo.page > this.#getLastPage() || pageInfo.page < 1) return false;

    return true;
  }

  #getLastPage() {
    const { grid } = this.refs;

    if (!grid) return 0;

    return Number(grid.getAttribute('last-page'));
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (!page || !this.#shouldUsePage(page)) return;

    this.pages.set(page.page, await sectionRenderer.getSectionHTML(this.sectionId, true, page.url));
    if (type === 'next') {
      this.#resolveNextPagePromise?.();
      this.#resolveNextPagePromise = null;
    } else {
      this.#resolvePreviousPagePromise?.();
      this.#resolvePreviousPagePromise = null;
    }
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });
      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });
      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * Process newly added elements and apply correct aspect ratios
   */
  #processNewElements() {
    // Wait for the DOM to update
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * Ensures the ratio stays within reasonable bounds and has consistent decimal places
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards
   * For the 'adapt' setting, each product should use its own image's aspect ratio
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);

        if (productId) {
          productRatioCache.set(productId, imgRatio);
        }

        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements
   * Only used for non-adaptive modes (square, portrait, landscape)
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    // Batch DOM operations for better performance
    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const page = isPrevious ? Number(targetCard.dataset.page) - 1 : Number(targetCard.dataset.page) + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }
}
